import { Flex } from "@chakra-ui/react";
import ErrorMsg from "../../common/ErrorMsg";
import { useApiTenant } from "../../context/ApiAuthContext";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import * as React from "react";
import { useQuery } from "react-query";
import { Redirect, Route, useParams, useRouteMatch } from "react-router-dom";
import { elementTreeApiClient, eventsApiClient } from "../../apiConfig";
import EventLayout from "../../common/EventLayout";
import Loader from "../../common/Loader";

import { _basePath } from "../../config";
import { UserContext } from "../../context";
import TimeSelection from "../TimeSelection";

function arrayFindById(arr, id) {
  return arr.find((p) => p.id === id);
}

/**
 * BuildEvent Tree
 * builds a nested json object of tree elements and children based on configured events
 * @param eventsArr {object[]} event array from elements API
 * @param elementTreeArray {object[]} elementTree array from elements API
 * @returns {object} nested json tree object
 */
function buildEventTree(eventsArr, elementTreeArray) {
  const now = DateTime.now();
  const makeTree = (items, id = null, link = "parentId") => {
    const filter = items.filter((item) => {
      const itemLink = item[link];
      return item && itemLink === id;
    });
    return filter.map((item) => {
      const children = makeTree(items, item.id);
      return { ...item, children: [...item.children, ...children] };
    });
  };
  const elementTreeObj = elementTreeArray.reduce((acc, cur) => {
    if (cur.parentId === "Equipment") {
      acc[cur.id] = cur;
    }
    return acc;
  }, {});

  function getParentElementTree(obj, arr) {
    const matches = arr.filter((item) => {
      return obj[item];
    });
    return matches[0];
  }
  const eventsObj = eventsArr.reduce((acc, ev) => {
    const StartsBefore = DateTime.fromISO(ev?.recurringStart) <= now;
    const EndsAfter = DateTime.fromISO(ev?.recurringEnd) >= now;

    if (
      !ev.elementTree ||
      !ev?.elementTree?.length ||
      !StartsBefore ||
      !ev?.recurringStart ||
      !EndsAfter ||
      !ev?.recurringEnd ||
      !ev?.slots ||
      ev?.slots === 0
    ) {
      return acc;
    }

    ev["isEvent"] = true;
    let elementTreeParent = getParentElementTree(
      elementTreeObj,
      ev.elementTree.reverse()
    );
    const treeItem = elementTreeObj[elementTreeParent];
    if (!treeItem) return acc;
    if (acc[treeItem.id] === undefined) {
      acc[treeItem.id] = {
        ...treeItem,
        ...{ children: [ev] },
      };
    } else {
      acc[treeItem.id].children.push(ev);
    }
    return acc;
  }, {});

  const newEventsArr = Object.keys(eventsObj).map((ev) => eventsObj[ev]);
  return makeTree(newEventsArr, "Equipment");
}

const EventCategory = ({ categoryData }) => {
  let { path } = useRouteMatch();
  return (
    <>
      <Route exact path={`${path}`}>
        <EventLayout events={categoryData} />
      </Route>
      <Route path={`${path}/:slug`}>
        <EventRouter cat={categoryData?.children} />
      </Route>
    </>
  );
};

const EventRouter = ({ cat }) => {
  const { slug } = useParams();
  const selection = arrayFindById(cat, slug) ?? { children: cat };

  const node =
    selection?.children?.length === 1 ? selection.children[0] : selection;

  return node.isEvent ? (
    <TimeSelection event={node} />
  ) : (
    <EventCategory categoryData={node} />
  );
};

const EventNavigation = () => {
  const { t } = useTranslation();
  const { tenantId } = useApiTenant();
  const { userData } = React.useContext(UserContext);
  const [selectionTree, setSelectionTree] = React.useState(null);

  const elementTreeQuery = useQuery(
    ["elementTree"],
    () =>
      elementTreeApiClient.userApiElementTreeListGet(
        undefined,
        tenantId,
        "1.0",
        "en-US,en"
      ),
    {
      select: (data) => data?.data?.data,
    }
  );
  const eventQuery = useQuery(
    ["events"],
    () =>
      eventsApiClient.userApiEventsGet(
        "Tenant",
        undefined,
        false,
        undefined,
        undefined,
        undefined,
        tenantId,
        "1.0",
        "en-US,en"
      ),
    {
      select: (data) => data?.data?.data,
      enabled: elementTreeQuery.isSuccess,
      onSuccess: (data) => {
        setSelectionTree(buildEventTree(data, elementTreeQuery.data));
      },
    }
  );

  const isLoading = elementTreeQuery.isLoading || eventQuery.isLoading;
  const isError = elementTreeQuery.isError || eventQuery.isError;

  if (!userData?.loggedIn) {
    return <Redirect to={_basePath} />;
  }

  if (isError) {
    const msg =
      elementTreeQuery.error?.response?.data?.detail ||
      eventQuery.error?.response?.data?.detail ||
      t(
        "error.unknown_error",
        "Unknown Error: Please contact a staff member for assistance."
      );
    return <ErrorMsg message={msg} />;
  }

  if (isLoading || !selectionTree) {
    return (
      <Flex w={"full"} h={"full"}>
        <Loader text={t("loader.getting_events", "Getting Events...")} />
      </Flex>
    );
  }

  return <EventRouter cat={selectionTree} />;
};

export default EventNavigation;
